import { Box } from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import {
  MarketplaceEntityTypeEnum,
  MonetizableEnum,
} from '@repo/supergraph-schemas/marketplace/enums';
import type { ProductCard as ProductCardType } from '@repo/supergraph-schemas/roundup/base/types';
import { dynamicWithRetry } from '../../../DynamicWithRetry';
import { SECTION_NAME } from '../ProductCard/ImageCtaReviewSection/constants';
import type { ProductCardProps } from '../ProductCard/types';

const ProductCard = dynamicWithRetry<ProductCardProps>(
  () => import('../ProductCard').then(mod => ({ default: mod.ProductCard })),
  { componentName: 'ProductCard' },
);

interface Props {
  cardBadgeText?: string;
  card: ProductCardType;
  cardIndex: number;
  productPositionBase: number;
  isSeoBankingFFActive: boolean;
  shouldApplyStylingUpdates?: boolean;
}

export default function ProductCardWrapper({
  cardBadgeText,
  card,
  cardIndex,
  productPositionBase,
  isSeoBankingFFActive,
  shouldApplyStylingUpdates = false,
}: Props): React.JSX.Element {
  const entity = card.marketplaceEntity;
  const elementPosition = cardIndex + productPositionBase;

  const overrideCta = useMemo(
    () => entity.ctaOverride || entity.cta,
    [entity.ctaOverride, entity.cta],
  );

  const {
    drivers,
    entityType,
    institution,
    monetizable: defaultMonetizable = MonetizableEnum.no,
    name,
    offerForCta,
    product,
    productType,
  } = entity;
  const monetizable = offerForCta?.monetizable ?? defaultMonetizable;
  const offerId = offerForCta?.id ? offerForCta.id : entity.id;
  const offerName = offerForCta?.name ? offerForCta.name : name;
  const ctaAnalytics = {
    index: elementPosition,
    ...(institution?.id && { institutionId: institution.id }),
    ...(entityType === MarketplaceEntityTypeEnum.Institutions && { institutionId: entity.id }),
    offerId,
    ...(product?.id && { productId: product.id }),
    productType,
    monetizable,
  };

  const marginTopStyles = cardIndex === 0 ? 0 : { xs: 5, md: 8 };

  return (
    <Box
      sx={{ marginTop: marginTopStyles, listStyle: 'none' }}
      key={`product-card-${elementPosition}`}
      component="li"
    >
      <AnalyticsSkuViewed
        source={SEGMENT_SKU_VIEWED_SOURCE}
        name={offerName}
        productType={productType}
        level="offer"
        institutionId={
          entityType === MarketplaceEntityTypeEnum.Institutions ? entity.id : entity.institution?.id
        }
        institutionName={
          entityType === MarketplaceEntityTypeEnum.Institutions ? name : entity.institution?.name
        }
        productId={entity.product?.id}
        productName={entity.product?.name}
        offerId={offerId}
        offerName={offerName}
        monetizable={monetizable}
        impressionSection={SECTION_NAME}
        impressionPosition={elementPosition}
        additionalAttributes={{
          page_type: 'roundup',
        }}
      >
        <div>
          <ProductCard
            ctaAnalytics={ctaAnalytics}
            bottomLine={card.bottomLine}
            cta={overrideCta}
            displayName={entity.displayName}
            drivers={drivers}
            federallyInsuredAnnotation={entity.federallyInsuredAnnotation}
            image={entity.image}
            monetizable={monetizable}
            starRating={entity.starRating}
            starRatingTooltip={entity.starRatingTooltip}
            reviewLink={entity.reviewLink}
            isSeoBankingFFActive={isSeoBankingFFActive}
            productType={entity.productType}
            subProductType={entity.subProductType}
            pros={card.pros}
            cons={card.cons}
            authorQuote={card.authorQuote}
            oneYearApyEntity={card.oneYearApyEntity}
            threeYearApyEntity={card.threeYearApyEntity}
            fiveYearApyEntity={card.fiveYearApyEntity}
            cardBadgeText={cardBadgeText}
            shouldApplyStylingUpdates={shouldApplyStylingUpdates}
            hideBestOfAwardsBanner={card.hideBestOfAwardsBanner}
            spotlightHeading={card.spotlightHeading}
            spotlightDetails={card.spotlightDetails}
          />
        </div>
      </AnalyticsSkuViewed>
    </Box>
  );
}
